import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { RefreshCw, GitCompare, TrendingUp, TrendingDown, Minus, Loader2, Zap, ArrowRight, Plus, X, Copy, Mail, Maximize2, Minimize2 } from "lucide-react";
import { startRescan, getAnalyzeStatus, getReport, getReportHistory, diffReports, getCredits, getWatchStatusForReport } from "@/lib/api";
import { toast } from "sonner";

// Premium waiting copy shown while a rescan runs (UI only — the scan itself is untouched).
// Mirrors the tone of the main scan screen; reveals no methodology.
const RESCAN_STEPS = [
  "Initializing rescan session",
  "Capturing the site's current state",
  "Running a full fresh analysis",
  "Benchmarking against your previous Blueprint",
  "Detecting what shifted since last time",
  "Preparing your updated Blueprint",
  "Final review in progress",
];

/**
 * RescanDiffPanel — shows on Results page for logged-in owners.
 * Handles: credit check, rescan action, history sidebar, and diff view.
 */
export default function RescanDiffPanel({ report, user, onRescanned }) {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [rescanStep, setRescanStep] = useState(0);
  const [confirmGap, setConfirmGap] = useState(null); // human-readable age of latest scan when < 24h
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [selectedCompare, setSelectedCompare] = useState(null);
  const [watchStatus, setWatchStatus] = useState(null);

  const isOwner = user && report.user_id && user.user_id === report.user_id;

  const refresh = useCallback(async () => {
    if (!isOwner) return;
    try {
      const [c, h] = await Promise.all([getCredits(), getReportHistory(report.id)]);
      setCredits(c);
      setHistory(h);
    } catch (_) { /* silent */ }
    finally { setHistoryLoading(false); }
    try {
      const ws = await getWatchStatusForReport(report.id);
      setWatchStatus(ws);
    } catch (_) { /* silent */ }
  }, [isOwner, report.id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Rotate the premium waiting copy while a rescan runs (last step holds)
  useEffect(() => {
    if (!rescanning) { setRescanStep(0); return; }
    const t = setInterval(() => {
      setRescanStep((s) => Math.min(s + 1, RESCAN_STEPS.length - 1));
    }, 9000);
    return () => clearInterval(t);
  }, [rescanning]);

  // Auto-diff against parent when this report was itself created via rescan
  useEffect(() => {
    if (!isOwner) return;
    if (!report.parent_report_id) return;
    if (diff) return;
    (async () => {
      setDiffLoading(true);
      try {
        const d = await diffReports(report.id, report.parent_report_id);
        setDiff(d);
        setSelectedCompare(report.parent_report_id);
      } catch (e) {
        // Silent — user can trigger manually
      } finally {
        setDiffLoading(false);
      }
    })();
  }, [isOwner, report.id, report.parent_report_id, diff]);

  if (!isOwner) return null;

  const hasSub = credits?.subscription_status === "active";
  const totalCredits = (credits?.total) ?? (credits?.credits ?? 0);
  // Monitored URLs get 1 free manual rescan per billing cycle — no credit needed
  const freeRescan = watchStatus?.state === "monitored" && watchStatus?.watch?.manual_rescan_available;
  const canRescan = totalCredits > 0 || freeRescan;

  const doRescan = async () => {
    setConfirmGap(null);
    setRescanning(true);
    try {
      // Start the rescan as a background job and poll — slow sites (bot-walled,
      // JS-heavy) can take 2+ minutes, which would hit the proxy's connection
      // timeout on a single blocking request.
      const { job_id, free_rescan } = await startRescan(report.id);
      if (free_rescan) toast.success("Included monitoring rescan — no credit used");
      const startedAt = Date.now();
      while (Date.now() - startedAt < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2500));
        let status;
        try {
          status = await getAnalyzeStatus(job_id);
        } catch {
          continue; // transient network blip — keep polling
        }
        if (status.status === "done" && status.report_id) {
          toast.success("Rescan complete. Loading diff...");
          const newReport = await getReport(status.report_id);
          if (onRescanned) onRescanned(newReport);
          navigate(`/report/${newReport.id}`);
          return;
        }
        if (status.status === "error") {
          throw new Error(status.error || "Rescan failed");
        }
      }
      throw new Error("Rescan is taking longer than expected. Check My Blueprints in a minute — it may still complete.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Rescan failed");
    } finally {
      setRescanning(false);
    }
  };

  const handleRescan = () => {
    if (!canRescan) {
      toast.info("No credits. Grab a pack or start the monthly.");
      navigate("/pricing");
      return;
    }
    // If the most recent Blueprint for this URL is < 24h old, real site movement is
    // unlikely — confirm before spending the lookup.
    const timestamps = [report.created_at, ...history.map((h) => h.created_at)].filter(Boolean);
    const latest = timestamps.length ? Math.max(...timestamps.map((t) => new Date(t).getTime())) : null;
    if (latest) {
      const hoursApart = (Date.now() - latest) / (1000 * 60 * 60);
      if (hoursApart >= 0 && hoursApart < 24) {
        let gap;
        if (hoursApart < 1) {
          const mins = Math.max(1, Math.round(hoursApart * 60));
          gap = `${mins} minute${mins === 1 ? "" : "s"}`;
        } else {
          const hrs = Math.max(1, Math.round(hoursApart));
          gap = `${hrs} hour${hrs === 1 ? "" : "s"}`;
        }
        setConfirmGap(gap);
        return;
      }
    }
    doRescan();
  };

  const compareTo = async (prevId) => {
    if (prevId === report.id) return;
    setDiffLoading(true);
    setSelectedCompare(prevId);
    try {
      const d = await diffReports(report.id, prevId);
      setDiff(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate diff");
      setSelectedCompare(null);
    } finally {
      setDiffLoading(false);
    }
  };

  const clearDiff = () => { setDiff(null); setSelectedCompare(null); };

  // History excluding the current report — potential comparison targets
  const olderScans = history.filter((h) => h.id !== report.id && new Date(h.created_at) < new Date(report.created_at));
  const hasHistory = olderScans.length > 0;

  return (
    <section className="border-b-2 border-[#111]" data-testid="rescan-diff-panel">
      {/* Early-rescan confirmation — latest Blueprint is < 24h old */}
      {confirmGap && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#111]/70 p-4"
          data-testid="rescan-confirm-overlay"
          onClick={() => setConfirmGap(null)}
        >
          <div
            className="w-full max-w-md bg-white border-2 border-[#111] shadow-brutal p-6"
            data-testid="rescan-confirm-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">
              ⚠ Heads up — recent Blueprint on file
            </div>
            <h3 className="mt-3 font-serif-display text-2xl font-black leading-tight">
              Your last Blueprint for this URL is only {confirmGap} old.
            </h3>
            <p className="mt-3 text-sm text-[#333] leading-relaxed">
              Websites usually take <strong>days or weeks</strong> to change meaningfully. Rescanning
              now will likely show scoring reshuffle rather than real site movement. This rescan uses{" "}
              <strong>{freeRescan ? "your included monthly rescan (free)" : "1 lookup"}</strong>.
            </p>
            <p className="mt-2 text-sm text-[#333]">Do you still want to continue?</p>
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={doRescan}
                className="inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
                data-testid="rescan-confirm-continue"
              >
                <RefreshCw size={13} /> Yes, continue
              </button>
              <button
                onClick={() => setConfirmGap(null)}
                className="inline-flex items-center gap-2 border-2 border-[#111] px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
                data-testid="rescan-confirm-cancel"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="border-2 border-[#111] bg-white">
          {/* Header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-5 md:p-6 border-b-2 border-[#111]">
            <div className="flex items-center gap-4">
              <div>
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                  § Time machine · This URL
                </div>
                <div className="mt-1 font-serif-display text-xl md:text-2xl font-bold leading-none">
                  {historyLoading ? "Loading history..." : `${history.length} Blueprint${history.length === 1 ? "" : "s"} on file`}
                </div>
                {rescanning && (
                  <div
                    className="mt-2 font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#FF331F] flex items-center gap-2"
                    data-testid="rescan-progress-line"
                    aria-live="polite"
                  >
                    <span className="inline-block w-1.5 h-1.5 bg-[#FF331F] animate-pulse" />
                    {RESCAN_STEPS[rescanStep]}...
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {credits && (
                <div className="font-mono-plex text-[10px] uppercase tracking-widest" data-testid="rescan-credits">
                  {hasSub ? (
                    <span className="inline-flex items-center gap-1 bg-[#FF331F] text-white px-2 py-1" title={`Monthly ${credits.monthly_credits} + Pack ${credits.credits}`}>
                      <Zap size={11} /> {credits.total} lookups · monthly
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 border-2 px-2 py-1 ${credits.credits > 0 ? "border-[#111] text-[#111]" : "border-[#FF331F] text-[#FF331F]"}`}>
                      <Zap size={11} /> {credits.credits} lookups
                    </span>
                  )}
                </div>
              )}
              <button
                onClick={handleRescan}
                disabled={rescanning || !canRescan}
                className="inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="rescan-button"
              >
                {rescanning ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Rescanning...
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} /> {freeRescan ? "Rescan Now — included free" : "Rescan (1 lookup)"}
                  </>
                )}
              </button>
              {!canRescan && (
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 border-2 border-[#111] px-4 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#111] hover:text-[#F4F4F0]"
                  data-testid="rescan-buy-credits"
                >
                  Get credits <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </div>

          {/* History timeline + compare picker */}
          {hasHistory && (
            <div className="p-5 md:p-6 border-b-2 border-[#111] bg-[#F4F4F0]">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
                ▸ Compare this Blueprint to an earlier one
              </div>
              <div className="flex flex-wrap gap-2" data-testid="history-timeline">
                {olderScans.slice(-8).map((h) => {
                  const top = h.recommendations?.[0];
                  const isSel = selectedCompare === h.id;
                  return (
                    <button
                      key={h.id}
                      onClick={() => compareTo(h.id)}
                      disabled={diffLoading}
                      className={`text-left p-3 border-2 transition-colors ${isSel ? "border-[#FF331F] bg-white" : "border-[#111] bg-white hover:bg-[#111] hover:text-[#F4F4F0]"} disabled:opacity-50`}
                      data-testid={`compare-btn-${h.id}`}
                    >
                      <div className="font-mono-plex text-[9px] uppercase tracking-widest">
                        {new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit" })}
                      </div>
                      {top && (
                        <div className="mt-1 font-mono-plex text-[10px]">
                          {String(top.monetization_type).replace(/_/g, " ")} · {top.fit_score}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Diff view */}
          {(diffLoading || diff) && (
            <div data-testid="diff-view">
              {diffLoading ? (
                <div
                  className="relative overflow-hidden bg-[#111] text-[#F4F4F0] p-6 md:p-8"
                  data-testid="diff-loading"
                >
                  <div
                    className="absolute inset-0 opacity-30 pointer-events-none"
                    style={{
                      backgroundImage: "radial-gradient(rgba(255,51,31,0.14) 1px, transparent 1px)",
                      backgroundSize: "6px 6px",
                    }}
                  />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 font-mono-plex text-[10px] uppercase tracking-[0.32em] text-[#F4F4F0]/60">
                      <span>§ MonetizeGPS</span>
                      <span className="flex-1 h-px bg-[#F4F4F0]/25" />
                      <span>Vol. Diff</span>
                    </div>
                    <h2 className="mt-5 font-serif-display text-5xl md:text-6xl font-black leading-[0.85] tracking-tight text-[#F4F4F0]">
                      The<br/><span className="text-[#FF331F] italic">Diff</span>
                    </h2>
                    <p className="mt-4 font-mono-plex text-xs md:text-sm text-[#F4F4F0]/75 max-w-xl">
                      Comparing two Blueprints of the same URL. Detecting what shifted, what appeared, and what dropped away.
                    </p>
                    <div className="mt-6 flex items-center gap-3 font-mono-plex text-xs uppercase tracking-widest text-[#FF331F]">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Computing changes...</span>
                    </div>
                  </div>
                </div>
              ) : diff ? (
                <div className="p-5 md:p-6">
                  <DiffContent
                    diff={diff}
                    onClose={clearDiff}
                    currentCreatedAt={report.created_at}
                    prevCreatedAt={history.find((h) => h.id === selectedCompare)?.created_at}
                    report={report}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DiffContent({ diff, onClose, currentCreatedAt, prevCreatedAt, report }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Compute how far apart the two Blueprints are. If < 24h, show a transparency banner
  // so the user understands most "changes" are AI reshuffling, not real site evolution.
  let noiseWarning = null;
  if (currentCreatedAt && prevCreatedAt) {
    const hoursApart = (new Date(currentCreatedAt) - new Date(prevCreatedAt)) / (1000 * 60 * 60);
    if (hoursApart >= 0 && hoursApart < 24) {
      let gap;
      if (hoursApart < 1) {
        const mins = Math.max(1, Math.round(hoursApart * 60));
        gap = `${mins} minute${mins === 1 ? "" : "s"}`;
      } else {
        const hrs = Math.round(hoursApart);
        gap = `${hrs} hour${hrs === 1 ? "" : "s"}`;
      }
      noiseWarning = gap;
    }
  }

  // Build a plain-text version of the Diff — used by Copy + Email.
  const buildDiffText = () => {
    const lines = [];
    const url = report?.url || "";
    const title = report?.title || url;
    const shareUrl = report ? `${window.location.origin}/report/${report.id}` : "";
    lines.push(`MonetizeGPS Diff — ${title}`);
    if (url) lines.push(url);
    lines.push("");
    lines.push(`▸ ${diff.headline || "Comparison ready."}`);
    if (diff.summary) { lines.push(""); lines.push(diff.summary); }
    if ((diff.score_deltas || []).length) {
      lines.push(""); lines.push("Score Changes (OLD → NEW)");
      diff.score_deltas.forEach((d) => {
        const arrow = d.direction === "up" ? "↑" : d.direction === "down" ? "↓" : "→";
        lines.push(`  • ${(d.monetization_type || "").toUpperCase()}: ${d.old_score} → ${d.new_score} (${arrow} ${Math.abs(d.delta || 0)})`);
      });
    }
    if ((diff.new_plays || []).length) {
      lines.push(""); lines.push("New Plays (Gained)");
      diff.new_plays.forEach((p) => lines.push(`  • ${(p.monetization_type || "").toUpperCase()} — fit ${p.fit_score}: ${p.why_new || ""}`));
    }
    if ((diff.removed_plays || []).length) {
      lines.push(""); lines.push("Dropped Plays (Lost)");
      diff.removed_plays.forEach((p) => lines.push(`  • ${(p.monetization_type || "").toUpperCase()} — was ${p.old_fit_score}: ${p.why_gone || ""}`));
    }
    if (diff.recommendation) { lines.push(""); lines.push(`Next Action: ${diff.recommendation}`); }
    if (shareUrl) { lines.push(""); lines.push(`Full Blueprint: ${shareUrl}`); }
    lines.push(`Sent from MonetizeGPS`);
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildDiffText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };

  const emailHref = (() => {
    const title = report?.title || report?.url || "URL";
    const subject = `MonetizeGPS Diff — ${title}`;
    const body = buildDiffText();
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  })();

  // Fullscreen toggle — when expanded, DiffContent takes over the viewport so it's easier to read.
  const wrapperClass = expanded
    ? "fixed inset-0 z-50 bg-white overflow-y-auto p-6 md:p-10"
    : "";
  return (
    <div className={wrapperClass}>
      {/* Editorial hero header — red background so the Diff feels distinct and premium */}
      <div className="relative bg-[#FF331F] text-[#F4F4F0] p-5 md:p-6 border-b-4 border-[#111] -mx-5 md:-mx-6 -mt-5 md:-mt-6 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 font-mono-plex text-[10px] uppercase tracking-[0.32em] text-[#F4F4F0]/85">
              <span>§ The Diff</span>
              <span className="flex-1 h-px bg-[#F4F4F0]/50" />
              <span className="whitespace-nowrap">What changed</span>
            </div>
            <h3
              className={`mt-3 font-serif-display font-black leading-[1.02] tracking-tight text-[#F4F4F0] ${expanded ? "text-4xl md:text-6xl" : "text-3xl md:text-4xl"}`}
              data-testid="diff-headline"
            >
              {diff.headline}
            </h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setExpanded((e) => !e)}
              className="p-2 hover:opacity-70 text-[#F4F4F0]"
              data-testid="diff-expand"
              aria-label={expanded ? "Collapse Diff" : "Expand Diff"}
              title={expanded ? "Collapse" : "Expand (fullscreen)"}
            >
              {expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:opacity-70 text-[#F4F4F0]"
              data-testid="diff-close"
              aria-label="Close diff"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Copy / Email actions — appear above the summary for quick access */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 border-2 border-[#111] px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0]"
          data-testid="diff-copy"
        >
          <Copy size={12} /> {copied ? "✓ Copied" : "Copy Diff"}
        </button>
        <a
          href={emailHref}
          className="inline-flex items-center gap-2 border-2 border-[#111] px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0]"
          data-testid="diff-email"
        >
          <Mail size={12} /> Email Diff
        </a>
      </div>

      <p className="text-sm md:text-[15px] text-[#333] leading-relaxed max-w-3xl" data-testid="diff-summary">
        {diff.summary}
      </p>

      {noiseWarning && (
        <div
          className="mt-4 border-2 border-dashed border-[#FF331F] bg-[#FFF5F3] p-3 md:p-4"
          data-testid="diff-noise-banner"
        >
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.24em] text-[#FF331F] font-bold">
            ⚠ Ranking Reshuffle — Not Site Change
          </div>
          <p className="mt-1 text-xs md:text-sm text-[#222] leading-relaxed">
            These Blueprints are <strong>{noiseWarning}</strong> apart. Most differences below are the scoring engine reshuffling, not the site actually changing. Diff is most useful when Blueprints are <strong>days or weeks apart</strong> and the URL has evolved.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {/* Score deltas */}
        <div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-2">
            ▸ Score movement
          </div>
          {diff.score_deltas?.length ? (
            <ul className="space-y-2" data-testid="diff-score-deltas">
              {diff.score_deltas.map((d, i) => (
                <li key={i} className="flex items-center justify-between text-sm border border-[#eee] px-3 py-2 bg-[#F4F4F0]">
                  <span className="capitalize truncate">{String(d.monetization_type).replace(/_/g, " ")}</span>
                  <DeltaBadge delta={d.delta} direction={d.direction} old={d.old_score} newVal={d.new_score} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#666]">No comparable plays.</p>
          )}
        </div>

        {/* New plays */}
        <div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#0A8754] mb-2">
            ▸ New opportunities
          </div>
          {diff.new_plays?.length ? (
            <ul className="space-y-2" data-testid="diff-new-plays">
              {diff.new_plays.map((p, i) => (
                <li key={i} className="border-l-2 border-[#0A8754] pl-3 py-1">
                  <div className="flex items-center gap-2">
                    <Plus size={12} className="text-[#0A8754]" />
                    <span className="font-mono-plex text-xs uppercase capitalize">{String(p.monetization_type).replace(/_/g, " ")} · {p.fit_score}</span>
                  </div>
                  <p className="mt-1 text-xs text-[#333]">{p.why_new}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#666]">Nothing new.</p>
          )}
        </div>

        {/* Removed plays */}
        <div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">
            ▸ Dropped from top
          </div>
          {diff.removed_plays?.length ? (
            <ul className="space-y-2" data-testid="diff-removed-plays">
              {diff.removed_plays.map((p, i) => (
                <li key={i} className="border-l-2 border-[#FF331F] pl-3 py-1">
                  <div className="flex items-center gap-2">
                    <X size={12} className="text-[#FF331F]" />
                    <span className="font-mono-plex text-xs uppercase capitalize">{String(p.monetization_type).replace(/_/g, " ")} · was {p.old_fit_score}</span>
                  </div>
                  <p className="mt-1 text-xs text-[#333]">{p.why_gone}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#666]">Nothing dropped.</p>
          )}
        </div>
      </div>

      {/* Signals + existing monetization changes */}
      {(diff.signal_changes?.added?.length || diff.signal_changes?.removed?.length ||
        diff.existing_monetization_changes?.added?.length || diff.existing_monetization_changes?.removed?.length) ? (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {(diff.signal_changes?.added?.length || diff.signal_changes?.removed?.length) ? (
            <div className="border border-[#eee] p-3">
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] mb-2">Signal changes</div>
              <div className="flex flex-wrap gap-1">
                {diff.signal_changes?.added?.map((s) => (
                  <span key={"a"+s} className="font-mono-plex text-[9px] uppercase tracking-widest border border-[#0A8754] text-[#0A8754] px-1.5 py-0.5">+ {s}</span>
                ))}
                {diff.signal_changes?.removed?.map((s) => (
                  <span key={"r"+s} className="font-mono-plex text-[9px] uppercase tracking-widest border border-[#FF331F] text-[#FF331F] px-1.5 py-0.5">− {s}</span>
                ))}
              </div>
            </div>
          ) : null}
          {(diff.existing_monetization_changes?.added?.length || diff.existing_monetization_changes?.removed?.length) ? (
            <div className="border border-[#eee] p-3">
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] mb-2">Existing monetization</div>
              <div className="flex flex-wrap gap-1">
                {diff.existing_monetization_changes?.added?.map((s) => (
                  <span key={"a"+s} className="font-mono-plex text-[9px] uppercase tracking-widest border border-[#0A8754] text-[#0A8754] px-1.5 py-0.5">+ {s}</span>
                ))}
                {diff.existing_monetization_changes?.removed?.map((s) => (
                  <span key={"r"+s} className="font-mono-plex text-[9px] uppercase tracking-widest border border-[#FF331F] text-[#FF331F] px-1.5 py-0.5">− {s}</span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {diff.recommendation && (
        <div className="mt-6 border-l-4 border-[#FF331F] bg-[#F4F4F0] p-4">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-1">▸ What to do next</div>
          <p className="text-sm text-[#222] leading-relaxed" data-testid="diff-recommendation">{diff.recommendation}</p>
        </div>
      )}
    </div>
  );
}

function DeltaBadge({ delta, direction, old, newVal }) {
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const color = direction === "up" ? "#0A8754" : direction === "down" ? "#FF331F" : "#555";
  const sign = delta > 0 ? "+" : "";
  return (
    <span className="flex items-center gap-2 shrink-0 font-mono-plex text-xs" style={{ color }}>
      <span className="text-[#555]">{old}</span>
      <Icon size={12} />
      <span className="font-bold">{newVal}</span>
      <span className="text-[10px]">({sign}{delta})</span>
    </span>
  );
}
